"use client";

import * as React from "react";
import { Box, Card, Flex, Spinner, Text } from "@radix-ui/themes";
import { ExternalLinkIcon, InfoCircledIcon } from "@radix-ui/react-icons";
import { Button, Skeleton } from "./elements.js";
import { GenericError } from "./generic-error.js";
import * as CardList from "./card-list.js";
import { IconPanel } from "./icon-panel.js";
import { ProviderIcon } from "./provider-icon.js";
import {
  getDirectoryTypeIcon,
  getDirectoryTypeName,
} from "./identity-providers.js";
import { Status } from "./status.js";
import { Translation } from "./i18n/translation.js";
import {
  getDomProps,
  type WidgetRootState,
  type WidgetRootDomProps,
} from "./utils.js";
import {
  DirectoryMetadata,
  DirectoryState,
  type DirectoryType,
} from "../api/endpoint.js";

interface BaseDirectorySyncProps {
  type?: DirectoryType;
  metadata?: DirectoryMetadata;
  lastSyncAt?: string | null;
  link?: string | null;
  errorMessage?: never;
  userCount?: never;
  groupCount?: never;
  currentDate?: never;
  state: DirectoryState;
}

interface NotConfiguredProps extends BaseDirectorySyncProps {
  state: (typeof DirectoryState)["unlinked"];
}

interface SetupInProgressProps extends Omit<BaseDirectorySyncProps, "type"> {
  state: (typeof DirectoryState)["validating"];
  type: DirectoryType;
}

interface ErrorBeforeConfigProps extends Omit<
  BaseDirectorySyncProps,
  "type" | "errorMessage"
> {
  state: DirectoryState;
  type: DirectoryType;
  errorMessage: React.ReactNode;
}

interface ValidatingProps extends Omit<BaseDirectorySyncProps, "type"> {
  state: (typeof DirectoryState)["validating"];
  type: DirectoryType;
}

interface ActiveProps extends Omit<
  BaseDirectorySyncProps,
  "type" | "lastSyncAt" | "currentDate" | "errorMessage"
> {
  state: (typeof DirectoryState)["linked"];
  type: DirectoryType;
  lastSyncAt: string | null;
  currentDate?: Date | null;
  errorMessage?: React.ReactNode;
}

interface InvalidCredentialsProps extends Omit<
  BaseDirectorySyncProps,
  "type" | "errorMessage"
> {
  state: (typeof DirectoryState)["invalid_credentials"];
  type: DirectoryType;
  errorMessage: React.ReactNode;
}

interface DeletingProps extends Omit<BaseDirectorySyncProps, "type"> {
  state: (typeof DirectoryState)["deleting"];
  type: DirectoryType;
}

type DirectorySyncStatusProps =
  | NotConfiguredProps
  | SetupInProgressProps
  | ErrorBeforeConfigProps
  | ValidatingProps
  | ActiveProps
  | InvalidCredentialsProps
  | DeletingProps;

type DirectorySyncProps = WidgetRootDomProps & DirectorySyncStatusProps;

interface DirectorySyncContextValue {
  state: DirectorySyncStatusProps["state"];
  type?: DirectoryType;
}

const DirectorySyncContext =
  React.createContext<DirectorySyncContextValue | null>(null);
DirectorySyncContext.displayName = "DirectorySyncContext";

function useDirectorySyncContext() {
  const context = React.useContext(DirectorySyncContext);
  if (!context) {
    throw new Error(
      "useDirectorySyncContext must be used within a DirectorySyncContext provider",
    );
  }
  return context;
}

function DirectoryProviderInfo({
  type,
  subtitle,
}: {
  type: DirectoryType;
  subtitle?: React.ReactNode;
}) {
  return (
    <Flex gap="4" align="center">
      <IconPanel color="panel">
        <ProviderIcon
          provider={getDirectoryTypeIcon(type) ?? "workos"}
          size="2"
        />
      </IconPanel>
      {subtitle ? (
        <Flex direction="column">
          <Text size="2" weight="bold">
            {getDirectoryTypeName(type)}
          </Text>
          {subtitle}
        </Flex>
      ) : (
        <Text size="2" weight="bold">
          {getDirectoryTypeName(type)}
        </Text>
      )}
    </Flex>
  );
}

function SpinnerMessage({ children }: { children: React.ReactNode }) {
  return (
    <Flex gap="2" align="center">
      <Spinner />
      <Text size="2" color="gray">
        {children}
      </Text>
    </Flex>
  );
}

function getStatusBadge(state: DirectoryState) {
  switch (state) {
    case DirectoryState.requires_type:
    case DirectoryState.invalid_credentials:
      return (
        <Status state="error">
          <Translation
            defaultMessage="Requires action"
            id="uuuXIF"
            description="Status badge when directory requires user action"
          />
        </Status>
      );
    case DirectoryState.linked:
      return (
        <Status state="success">
          <Translation
            defaultMessage="Connected"
            id="H/dAHq"
            description="Status badge when directory is active"
          />
        </Status>
      );
    default:
      return null;
  }
}

function getSpinnerMessage(
  state: DirectoryState,
  hasType: boolean,
): string | null {
  if (state === DirectoryState.unlinked && hasType)
    return "Setup in progress...";
  if (state === DirectoryState.validating)
    return "Waiting for users and groups...";
  if (state === DirectoryState.deleting) return "Deleting directory...";
  return null;
}

const DirectorySync: React.FC<DirectorySyncProps> = ({
  state,
  className,
  ...rest
}) => {
  const type =
    "type" in rest ? (rest as { type: DirectoryType }).type : undefined;
  const errorMessage =
    "errorMessage" in rest
      ? (rest as { errorMessage: React.ReactNode }).errorMessage
      : undefined;
  const metadata =
    "metadata" in rest
      ? (rest as { metadata: DirectoryMetadata }).metadata
      : undefined;
  const link =
    "link" in rest ? (rest as { link: string | null }).link : undefined;
  const userCount = metadata?.users.active ?? 0;
  const groupCount = metadata?.groups ?? 0;
  const lastSyncAt = rest.lastSyncAt ? new Date(rest.lastSyncAt) : null;

  const currentDate =
    "currentDate" in rest && rest.currentDate instanceof Date
      ? rest.currentDate
      : undefined;

  const lastSyncString = lastSyncAt
    ? getRelativeTimeString(currentDate ?? new Date(), lastSyncAt)
    : null;

  const showCounts =
    state === DirectoryState.linked ||
    state === DirectoryState.invalid_credentials ||
    state === DirectoryState.deleting;
  const statusBadge = getStatusBadge(state);
  const spinnerMessage = getSpinnerMessage(state, !!type);
  const showButton = state !== DirectoryState.deleting;
  const domProps: WidgetRootDomProps = { className };

  const countsSubtitle = showCounts ? (
    <Text size="2" color="gray">
      <Translation
        defaultMessage="{userCount} {userLabel}"
        id="aZxx55"
        description="Directory sync user and group counts"
        values={{
          userCount,
          userLabel: userCount !== 1 ? "users" : "user",
        }}
      />
      {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
      <span aria-hidden> ∙ </span>
      <Translation
        defaultMessage="{groupCount} {groupLabel}"
        id="SgiDio"
        description="Directory sync group count"
        values={{
          groupCount,
          groupLabel: groupCount !== 1 ? "groups" : "group",
        }}
      />
      {lastSyncString && (
        <>
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
          <span aria-hidden> ∙ </span>
          <Translation
            defaultMessage="Last sync {lastSync}"
            id="UUqjnE"
            description="Last sync timestamp for directory"
            values={{ lastSync: lastSyncString }}
          />
        </>
      )}
    </Text>
  ) : null;

  return (
    <CardList.Root {...getWidgetRootDomProps("resolved", domProps)}>
      <DirectorySyncContext.Provider value={{ state, type }}>
        <CardList.Item>
          <Flex direction="row" justify="between" align="center" gap="2">
            {!type ? (
              <>
                <Text size="2" color="gray">
                  <Translation
                    defaultMessage="You haven't set up a directory yet."
                    id="+ywYVl"
                    description="Empty state message when no directory is configured"
                  />
                </Text>
                <AdminPortalOpenButton href={link ?? null} />
              </>
            ) : (
              <>
                <DirectoryProviderInfo type={type} subtitle={countsSubtitle} />
                <Flex gap="5" align="center">
                  {statusBadge}
                  {spinnerMessage && (
                    <SpinnerMessage>{spinnerMessage}</SpinnerMessage>
                  )}
                  {showButton && <AdminPortalOpenButton href={link ?? null} />}
                </Flex>
              </>
            )}
          </Flex>
        </CardList.Item>

        {errorMessage && (
          <CardList.Item>
            <Flex align="start" gap="2">
              <Box asChild mt="2px" flexShrink="0">
                <InfoCircledIcon color="var(--gray-11)" />
              </Box>
              <Text size="2" color="gray">
                {errorMessage}
              </Text>
            </Flex>
          </CardList.Item>
        )}
      </DirectorySyncContext.Provider>
    </CardList.Root>
  );
};

function AdminPortalOpenButton({
  href,
  children,
}: {
  href: string | null;
  children?: React.ReactNode;
}) {
  const { state, type } = useDirectorySyncContext();

  const label = (() => {
    switch (state) {
      case DirectoryState.unlinked:
        return type ? "Continue setup" : "Set up directory";
      case DirectoryState.validating:
        return "Manage";
      default:
        return "Manage";
    }
  })();

  if (href) {
    return (
      <Button variant="secondary" asChild>
        <a href={href} target="_blank" rel="noopener noreferrer">
          {children ?? label} <ExternalLinkIcon aria-hidden />
        </a>
      </Button>
    );
  }

  return (
    <Button variant="secondary" disabled>
      {children ?? label} <ExternalLinkIcon aria-hidden />
    </Button>
  );
}

interface DirectorySyncLoadingProps extends WidgetRootDomProps {}

const DirectorySyncLoading: React.FC<DirectorySyncLoadingProps> = (props) => {
  return (
    <Card size="2" {...getWidgetRootDomProps("loading", props)}>
      <Flex direction="row" justify="between" align="center" gap="2">
        <Skeleton>
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
          <Text size="2" color="gray">
            You haven't set up a directory yet.
          </Text>
        </Skeleton>
        <Skeleton>
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
          <Button variant="secondary">
            Set up directory <ExternalLinkIcon aria-hidden />
          </Button>
        </Skeleton>
      </Flex>
    </Card>
  );
};

interface DirectorySyncErrorProps extends WidgetRootDomProps {
  error: unknown;
}

const DirectorySyncError: React.FC<DirectorySyncErrorProps> = ({
  error,
  ...domProps
}) => {
  return (
    <Card size="2" {...getWidgetRootDomProps("error", domProps)}>
      <GenericError error={error} />
    </Card>
  );
};

function getWidgetRootDomProps(
  state: WidgetRootState,
  domProps: WidgetRootDomProps,
) {
  return getDomProps({
    ...domProps,
    isWidgetRoot: true,
    widgetId: "directory-sync",
    widgetState: state,
  });
}

function getRelativeTimeString(
  currentDate: Date | null,
  lastSync: Date | null,
) {
  if (!currentDate || !lastSync) {
    return null;
  }

  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  const diff = lastSync.getTime() - currentDate.getTime();

  const diffSeconds = Math.round(diff / 1000);
  const diffMinutes = Math.round(diffSeconds / 60);
  const diffHours = Math.round(diffMinutes / 60);
  const diffDays = Math.round(diffHours / 24);
  const diffMonths = Math.round(diffDays / 30);
  const diffYears = Math.round(diffMonths / 12);

  if (Math.abs(diffSeconds) < 60) {
    return "now";
  }

  if (Math.abs(diffMinutes) < 60) {
    return rtf.format(diffMinutes, "minute");
  }

  if (Math.abs(diffHours) < 24) {
    return rtf.format(diffHours, "hour");
  }

  if (Math.abs(diffDays) < 30) {
    return rtf.format(diffDays, "day");
  }

  if (Math.abs(diffMonths) < 12) {
    return rtf.format(diffMonths, "month");
  }

  return rtf.format(diffYears, "year");
}

export type {
  DirectorySyncProps,
  DirectorySyncLoadingProps,
  DirectorySyncErrorProps,
  DirectorySyncStatusProps,
};
export { DirectorySync, DirectorySyncLoading, DirectorySyncError };
